import { Route, Switch, useLocation } from "wouter";
import { useEffect, useRef, useState, lazy, Suspense } from "react";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import AlternativeGuides from "./components/AlternativeGuides";
import CommercialStickyBookBar from "./components/CommercialStickyBookBar";
import TreatmentBookingLinkDecorator from "./components/TreatmentBookingLinkDecorator";
import { trackMetaEvent } from "./lib/gtm";
import LazyImageFadeController from "./components/LazyImageFadeController";
import FirstTimerBanner from "./components/FirstTimerBanner";
import { ROUTE_REDIRECTS } from "@shared/routePolicy";
import { lazyPage } from "./lib/lazyPage";

// SEOSchema renders nothing — its effect rewrites <head> meta and JSON-LD — so it
// does not need to ship in the entry chunk; lazyPage keeps the SSR pass identical.
// It is not modulepreloaded: the server already wrote this route's <head>, so the
// refresh can wait until after the page chunk instead of competing with the LCP image.
const SEOSchema = lazyPage("src/components/SEOSchema.tsx", () => import("./components/SEOSchema"), {
  modulePreload: false,
});

// Floating patient widgets and the toast host are not needed for first paint.
// They load on the first interaction or once the browser is idle.
const ChatWidget = lazy(() => import("./components/ChatWidget"));
const CherryFloating = lazy(() => import("./components/CherryFinancing").then(m => ({ default: m.CherryFloating })));
const Toaster = lazy(() => import("sonner").then(m => ({ default: m.Toaster })));


// ── Public routes (code-split, still fully server-rendered) ──────────
// Sitemap-backed routes must reach crawlers with the real page body, never a
// Suspense fallback. lazyPage gives each page its own chunk while entry-server
// re-renders until every page import has resolved (see lib/lazyPage.tsx).
const LandingPage = lazyPage("src/pages/LandingPage.tsx", () => import("./pages/LandingPage"));
const NotFound = lazyPage("src/pages/NotFound.tsx", () => import("./pages/NotFound"));
const WeightLoss = lazyPage("src/pages/WeightLoss.tsx", () => import("./pages/WeightLoss"));
const BodySculpting = lazyPage("src/pages/BodySculpting.tsx", () => import("./pages/BodySculpting"));
const StubbornFat = lazyPage("src/pages/StubbornFat.tsx", () => import("./pages/StubbornFat"));
const Cryolipolysis = lazyPage("src/pages/Cryolipolysis.tsx", () => import("./pages/Cryolipolysis"));
const DplPhotofacial = lazyPage("src/pages/DplPhotofacial.tsx", () => import("./pages/DplPhotofacial"));
const DuoVive = lazyPage("src/pages/DuoVive.tsx", () => import("./pages/DuoVive"));
const HairGrowth = lazyPage("src/pages/HairGrowth.tsx", () => import("./pages/HairGrowth"));
const FractionalCO2 = lazyPage("src/pages/FractionalCO2.tsx", () => import("./pages/FractionalCO2"));
const SkinAnalysis = lazyPage("src/pages/SkinAnalysis.tsx", () => import("./pages/SkinAnalysis"));
const PicoLaser = lazyPage("src/pages/PicoLaser.tsx", () => import("./pages/PicoLaser"));
const RFMicroneedling = lazyPage("src/pages/RFMicroneedling.tsx", () => import("./pages/RFMicroneedling"));
const Aquafacial = lazyPage("src/pages/Aquafacial.tsx", () => import("./pages/Aquafacial"));
const StretchMark = lazyPage("src/pages/StretchMark.tsx", () => import("./pages/StretchMark"));
const WellnessAntiAging = lazyPage("src/pages/WellnessAntiAging.tsx", () => import("./pages/WellnessAntiAging"));
const HormoneOptimization = lazyPage("src/pages/HormoneOptimization.tsx", () => import("./pages/HormoneOptimization"));
const PeptideTherapy = lazyPage("src/pages/PeptideTherapy.tsx", () => import("./pages/PeptideTherapy"));
const FunctionalLabTesting = lazyPage("src/pages/FunctionalLabTesting.tsx", () => import("./pages/FunctionalLabTesting"));
const SkinLaxity = lazyPage("src/pages/SkinLaxity.tsx", () => import("./pages/SkinLaxity"));
const HIFUPage = lazyPage("src/pages/HIFU.tsx", () => import("./pages/HIFU"));
const XerfSkinTightening = lazyPage("src/pages/XerfSkinTightening.tsx", () => import("./pages/XerfSkinTightening"));
const ProviderPartners = lazyPage("src/pages/ProviderPartners.tsx", () => import("./pages/ProviderPartners"));
const ProviderPartnersThankYou = lazyPage("src/pages/ProviderPartnersThankYou.tsx", () => import("./pages/ProviderPartnersThankYou"));
const AcneScars = lazyPage("src/pages/AcneScars.tsx", () => import("./pages/AcneScars"));
const FaceSkinConditions = lazyPage("src/pages/FaceSkinConditions.tsx", () => import("./pages/FaceSkinConditions"));
const AcneBreakouts = lazyPage("src/pages/AcneBreakouts.tsx", () => import("./pages/AcneBreakouts"));
const EnlargedPores = lazyPage("src/pages/EnlargedPores.tsx", () => import("./pages/EnlargedPores"));
const Pigmentation = lazyPage("src/pages/Pigmentation.tsx", () => import("./pages/Pigmentation"));
const AresLiteHairRemoval = lazyPage("src/pages/AresLiteHairRemoval.tsx", () => import("./pages/AresLiteHairRemoval"));
const RenaSculpt = lazyPage("src/pages/RenaSculpt.tsx", () => import("./pages/RenaSculpt"));
const DiodeLaserLipolysis = lazyPage("src/pages/DiodeLaserLipolysis.tsx", () => import("./pages/DiodeLaserLipolysis"));
const LLLTHairRestoration = lazyPage("src/pages/LLLTHairRestoration.tsx", () => import("./pages/LLLTHairRestoration"));
const FineLines = lazyPage("src/pages/FineLines.tsx", () => import("./pages/FineLines"));
const RednessRosacea = lazyPage("src/pages/RednessRosacea.tsx", () => import("./pages/RednessRosacea"));
const DermalFillers = lazyPage("src/pages/DermalFillers.tsx", () => import("./pages/DermalFillers"));
const Neuromodulators = lazyPage("src/pages/Neuromodulators.tsx", () => import("./pages/Neuromodulators"));
const ChemicalPeels = lazyPage("src/pages/ChemicalPeels.tsx", () => import("./pages/ChemicalPeels"));
const Kybella = lazyPage("src/pages/Kybella.tsx", () => import("./pages/Kybella"));
const PDOThreads = lazyPage("src/pages/PDOThreads.tsx", () => import("./pages/PDOThreads"));
const VaginalRejuvenation = lazyPage("src/pages/VaginalRejuvenation.tsx", () => import("./pages/VaginalRejuvenation"));
const PRPSexualWellness = lazyPage("src/pages/PRPSexualWellness.tsx", () => import("./pages/PRPSexualWellness"));
const PelvicFloor = lazyPage("src/pages/PelvicFloor.tsx", () => import("./pages/PelvicFloor"));
const Biostimulators = lazyPage("src/pages/Biostimulators.tsx", () => import("./pages/Biostimulators"));
const LipFillers = lazyPage("src/pages/LipFillers.tsx", () => import("./pages/LipFillers"));
const PainRegenerativeMedicine = lazyPage("src/pages/PainRegenerativeMedicine.tsx", () => import("./pages/PainRegenerativeMedicine"));
const Injectables = lazyPage("src/pages/Injectables.tsx", () => import("./pages/Injectables"));
const FaceSkinRejuvenation = lazyPage("src/pages/FaceSkinRejuvenation.tsx", () => import("./pages/FaceSkinRejuvenation"));
const IntimateWellness = lazyPage("src/pages/IntimateWellness.tsx", () => import("./pages/IntimateWellness"));
const BodyContouring = lazyPage("src/pages/BodyContouring.tsx", () => import("./pages/BodyContouring"));
const Membership = lazyPage("src/pages/Membership.tsx", () => import("./pages/Membership"));
const SpecialsAndNew = lazyPage("src/pages/SpecialsAndNew.tsx", () => import("./pages/SpecialsAndNew"));
const PrivacyPolicy = lazyPage("src/pages/PrivacyPolicy.tsx", () => import("./pages/PrivacyPolicy"));
const MedicalDisclaimer = lazyPage("src/pages/MedicalDisclaimer.tsx", () => import("./pages/MedicalDisclaimer"));
const TechnologyComparison = lazyPage("src/pages/TechnologyComparison.tsx", () => import("./pages/TechnologyComparison"));
const AISkinAnalysis = lazyPage("src/pages/AISkinAnalysis.tsx", () => import("./pages/AISkinAnalysis"));
const DarkCircles = lazyPage("src/pages/DarkCircles.tsx", () => import("./pages/DarkCircles"));
const FacialVolume = lazyPage("src/pages/FacialVolume.tsx", () => import("./pages/FacialVolume"));
const Blog = lazyPage("src/pages/Blog.tsx", () => import("./pages/Blog"));
const BlogPost = lazyPage("src/pages/BlogPost.tsx", () => import("./pages/BlogPost"));
const TermsOfService = lazyPage("src/pages/TermsOfService.tsx", () => import("./pages/TermsOfService"));
const Contact = lazyPage("src/pages/Contact.tsx", () => import("./pages/Contact"));
const SMSTerms = lazyPage("src/pages/SMSTerms.tsx", () => import("./pages/SMSTerms"));
const DrKohli = lazyPage("src/pages/DrKohli.tsx", () => import("./pages/DrKohli"));
const WhyChooseUs = lazyPage("src/pages/WhyChooseUs.tsx", () => import("./pages/WhyChooseUs"));
const AreasWeServe = lazyPage("src/pages/AreasWeServe.tsx", () => import("./pages/AreasWeServe"));
const MicroneedlingDallas = lazyPage("src/pages/AdditiveSeoPages.tsx", () => import("./pages/AdditiveSeoPages").then(m => ({ default: m.MicroneedlingDallas })));
const MedSpaNearMeDallas = lazyPage("src/pages/AdditiveSeoPages.tsx", () => import("./pages/AdditiveSeoPages").then(m => ({ default: m.MedSpaNearMeDallas })));
const NonSurgicalFaceLift = lazyPage("src/pages/FaceLiftPages.tsx", () => import("./pages/FaceLiftPages").then(m => ({ default: m.NonSurgicalFaceLift })));
const FacialBalancing = lazyPage("src/pages/FaceLiftPages.tsx", () => import("./pages/FaceLiftPages").then(m => ({ default: m.FacialBalancing })));
const SkinTighteningCompared = lazyPage("src/pages/SkinTighteningCompared.tsx", () => import("./pages/SkinTighteningCompared").then(m => ({ default: m.SkinTighteningCompared })));
const JawlineFillerDallas = lazyPage("src/pages/ServiceGapPages.tsx", () => import("./pages/ServiceGapPages").then(m => ({ default: m.JawlineFillerDallas })));
const TearTroughFillerDallas = lazyPage("src/pages/ServiceGapPages.tsx", () => import("./pages/ServiceGapPages").then(m => ({ default: m.TearTroughFillerDallas })));
const NasolabialFoldsDallas = lazyPage("src/pages/ServiceGapPages.tsx", () => import("./pages/ServiceGapPages").then(m => ({ default: m.NasolabialFoldsDallas })));
const HandFillerDallas = lazyPage("src/pages/ServiceGapPages.tsx", () => import("./pages/ServiceGapPages").then(m => ({ default: m.HandFillerDallas })));
const TurkeyNeckDallas = lazyPage("src/pages/ServiceGapPages.tsx", () => import("./pages/ServiceGapPages").then(m => ({ default: m.TurkeyNeckDallas })));
const FinancingPage = lazyPage("src/pages/OrganicGrowthPages.tsx", () => import("./pages/OrganicGrowthPages").then(m => ({ default: m.FinancingPage })));
const GalleryHub = lazyPage("src/pages/OrganicGrowthPages.tsx", () => import("./pages/OrganicGrowthPages").then(m => ({ default: m.GalleryHub })));
const GalleryRoute = lazyPage("src/pages/OrganicGrowthPages.tsx", () => import("./pages/OrganicGrowthPages").then(m => ({ default: m.GalleryRoute })));
const PackagesPage = lazyPage("src/pages/OrganicGrowthPages.tsx", () => import("./pages/OrganicGrowthPages").then(m => ({ default: m.PackagesPage })));
const LaserHairRemovalDallas = lazyPage("src/pages/lp/LaserHairRemovalDallas.tsx", () => import("./pages/lp/LaserHairRemovalDallas"));
const FacialBalancingDallas = lazyPage("src/pages/lp/FacialBalancingDallas.tsx", () => import("./pages/lp/FacialBalancingDallas"));
const LiquidFaceliftDallas = lazyPage("src/pages/lp/LiquidFaceliftDallas.tsx", () => import("./pages/lp/LiquidFaceliftDallas"));

// ── Lazily loaded auxiliary, admin, and noindex routes ─────────────────
const DeviceDetail            = lazy(() => import("./pages/DeviceDetail"));
const LeadsDashboard          = lazy(() => import("./pages/LeadsDashboard"));
const AdminBlog               = lazy(() => import("./pages/AdminBlog"));

// ── Internal MedSpa Management Dashboard ─────────────────────────────
const AdminDashboard          = lazy(() => import("./pages/admin/Dashboard"));
const AdminAppointments       = lazy(() => import("./pages/admin/Appointments"));
const AdminPatients           = lazy(() => import("./pages/admin/Patients"));
const AdminRevenue            = lazy(() => import("./pages/admin/Revenue"));
const AdminServices           = lazy(() => import("./pages/admin/Services"));
const AdminLeads              = lazy(() => import("./pages/admin/Leads"));
const AdminGoogleIndexing     = lazy(() => import("./pages/admin/GoogleIndexing"));
const AIContext               = lazy(() => import("./pages/AIContext"));

// ── Google Ads Landing Pages (no nav, conversion-focused) ─────────────
const RFMicroneedlingDallas = lazyPage("src/pages/lp/RFMicroneedlingDallas.tsx", () => import("./pages/lp/RFMicroneedlingDallas"));
const IPLPhotofacialDallas = lazyPage("src/pages/lp/IPLPhotofacialDallas.tsx", () => import("./pages/lp/IPLPhotofacialDallas"));
const CO2LaserDallas = lazyPage("src/pages/lp/CO2LaserDallas.tsx", () => import("./pages/lp/CO2LaserDallas"));
const BestMedSpaDallas = lazyPage("src/pages/lp/BestMedSpaDallas.tsx", () => import("./pages/lp/BestMedSpaDallas"));
const AquafacialDallas = lazyPage("src/pages/lp/AquafacialDallas.tsx", () => import("./pages/lp/AquafacialDallas"));
const DermalFillersDallas = lazyPage("src/pages/lp/DermalFillersDallas.tsx", () => import("./pages/lp/DermalFillersDallas"));
const RFMicroneedlingAds = lazyPage("src/pages/lp/RFMicroneedlingAds.tsx", () => import("./pages/lp/RFMicroneedlingAds"));
const PRPHairDallas = lazyPage("src/pages/lp/PRPHairDallas.tsx", () => import("./pages/lp/PRPHairDallas"));

// ── Brand-Name Alternative SEO Pages (eagerly loaded — small components) ────
const Morpheus8Alternative = lazyPage("src/pages/alternatives/index.tsx", () => import("./pages/alternatives/index").then(m => ({ default: m.Morpheus8Alternative })));
const BBLAlternative = lazyPage("src/pages/alternatives/index.tsx", () => import("./pages/alternatives/index").then(m => ({ default: m.BBLAlternative })));
const FraxelAlternative = lazyPage("src/pages/alternatives/index.tsx", () => import("./pages/alternatives/index").then(m => ({ default: m.FraxelAlternative })));
const AquaFacialAlternative = lazyPage("src/pages/alternatives/index.tsx", () => import("./pages/alternatives/index").then(m => ({ default: m.AquaFacialAlternative })));
const UltherapyAlternative = lazyPage("src/pages/alternatives/index.tsx", () => import("./pages/alternatives/index").then(m => ({ default: m.UltherapyAlternative })));
const SculptraAlternative = lazyPage("src/pages/alternatives/index.tsx", () => import("./pages/alternatives/index").then(m => ({ default: m.SculptraAlternative })));
const CoolPeelAlternative = lazyPage("src/pages/alternatives/index.tsx", () => import("./pages/alternatives/index").then(m => ({ default: m.CoolPeelAlternative })));
const ClearBrilliantAlternative = lazyPage("src/pages/alternatives/index.tsx", () => import("./pages/alternatives/index").then(m => ({ default: m.ClearBrilliantAlternative })));
const EmsculptNeoAlternative = lazyPage("src/pages/alternatives/index.tsx", () => import("./pages/alternatives/index").then(m => ({ default: m.EmsculptNeoAlternative })));
const PicoSureAlternative = lazyPage("src/pages/alternatives/index.tsx", () => import("./pages/alternatives/index").then(m => ({ default: m.PicoSureAlternative })));

// ── Instagram Bio Link & Referral ─────────────────────────────────────
const InstagramBioLink        = lazy(() => import("./pages/InstagramBioLink"));
const Booking                  = lazy(() => import("./pages/Booking"));
const Referral                = lazy(() => import("./pages/Referral"));
const LeaveAReview            = lazy(() => import("./pages/LeaveAReview"));

// ── Page loading fallback ─────────────────────────────────────────────
function PageLoader() {
  return (
    <div style={{
      minHeight: "60vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "var(--ivory, #F6F1EA)",
    }}>
      <div style={{
        width: 40,
        height: 40,
        border: "3px solid #e8ddd0",
        borderTopColor: "#5B7A9E",
        borderRadius: "50%",
        animation: "spin 0.8s linear infinite",
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function ScrollToTop() {
  const [location] = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" as ScrollBehavior });
  }, [location]);
  return null;
}

function SharedPolicyRedirect({ from }: { from: string }) {
  const [, navigate] = useLocation();
  useEffect(() => {
    const target = ROUTE_REDIRECTS[from];
    if (!target) return;
    if (/^https?:\/\//.test(target)) {
      window.location.replace(target);
      return;
    }
    navigate(target, { replace: true });
  }, [from, navigate]);
  return null;
}
function PixelPageView() {
  const [location] = useLocation();
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    trackMetaEvent("PageView", { page_path: location }, `spa_pageview_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`);
  }, [location]);
  return null;
}

function Layout({ children }: { children: React.ReactNode }) {
  const [location] = useLocation();
  const hideFirstTimerBanner = location.startsWith("/lp/") || location === "/xerf-skin-tightening" || location === "/membership";
  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", backgroundColor: "var(--ivory)" }}>
      {/* Skip-to-content for keyboard/screen-reader accessibility */}
      <a
        href="#main-content"
        style={{
          position: "absolute",
          top: "-100px",
          left: "1rem",
          zIndex: 9999,
          padding: "0.5rem 1rem",
          background: "#5B7A9E",
          color: "#fff",
          borderRadius: "4px",
          fontSize: "0.875rem",
          fontWeight: 600,
          textDecoration: "none",
          transition: "top 0.2s",
        }}
        onFocus={e => (e.currentTarget.style.top = "1rem")}
        onBlur={e => (e.currentTarget.style.top = "-100px")}
      >
        Skip to main content
      </a>
      <Navbar />
      {!hideFirstTimerBanner && <FirstTimerBanner />}
      <CommercialStickyBookBar />
      <main id="main-content" style={{ flex: 1 }}>
        {/* The decorator rewrites booking anchors in the DOM, so it shares the page's
            boundary: it must not touch server HTML that is still waiting to hydrate. */}
        <Suspense fallback={<PageLoader />}>
          {children}
          <TreatmentBookingLinkDecorator />
        </Suspense>
      </main>
      <AlternativeGuides />
      <Footer />

    </div>
  );
}

function Router() {
  // make sure to consider if you need authentication for certain routes
  return (
    <>
    <ScrollToTop />
    <PixelPageView />
    <LazyImageFadeController />
    <Suspense fallback={null}>
      <SEOSchema />
    </Suspense>
    <Suspense fallback={<PageLoader />}>
    <Switch>
      {Object.keys(ROUTE_REDIRECTS).map(path => (
        <Route key={path} path={path} component={() => <SharedPolicyRedirect from={path} />} />
      ))}
      {/* ── Homepage ── */}
      <Route path="/" component={() => <Layout><LandingPage /></Layout>} />

      {/* ── Face & Skin Rejuvenation ── */}
      <Route path="/face-skin-rejuvenation" component={() => <Layout><FaceSkinRejuvenation /></Layout>} />
      <Route path="/rf-microneedling" component={() => <Layout><RFMicroneedling /></Layout>} />
      <Route path="/fractional-co2" component={() => <Layout><FractionalCO2 /></Layout>} />
      <Route path="/duovive" component={() => <Layout><DuoVive /></Layout>} />
      <Route path="/dpl-photofacial" component={() => <Layout><DplPhotofacial /></Layout>} />
      <Route path="/pico-laser" component={() => <Layout><PicoLaser /></Layout>} />
      <Route path="/aquafacial" component={() => <Layout><Aquafacial /></Layout>} />
      <Route path="/skin-analysis" component={() => <Layout><SkinAnalysis /></Layout>} />
      <Route path="/stretch-mark" component={() => <Layout><StretchMark /></Layout>} />
      <Route path="/skin-laxity" component={() => <Layout><SkinLaxity /></Layout>} />
      <Route path="/microneedling" component={() => <Layout><MicroneedlingDallas /></Layout>} />
      <Route path="/hifu" component={() => <Layout><HIFUPage /></Layout>} />
      <Route path="/xerf-skin-tightening" component={() => <Layout><XerfSkinTightening /></Layout>} />
      {/* Provider-partner recruitment funnel (standalone, no patient navigation) */}
      <Route path="/provider-partners/thank-you" component={ProviderPartnersThankYou} />
      <Route path="/provider-partners" component={ProviderPartners} />
      <Route path="/specials" component={() => <Layout><SpecialsAndNew /></Layout>} />

      {/* ── Skin Conditions ── */}
      <Route path="/face-skin-conditions" component={() => <Layout><FaceSkinConditions /></Layout>} />
      <Route path="/acne-scars" component={() => <Layout><AcneScars /></Layout>} />
      <Route path="/acne-breakouts" component={() => <Layout><AcneBreakouts /></Layout>} />
      <Route path="/enlarged-pores" component={() => <Layout><EnlargedPores /></Layout>} />
      <Route path="/pigmentation" component={() => <Layout><Pigmentation /></Layout>} />
      <Route path="/fine-lines-wrinkles" component={() => <Layout><FineLines /></Layout>} />
      <Route path="/redness-rosacea" component={() => <Layout><RednessRosacea /></Layout>} />
      <Route path="/dark-circles" component={() => <Layout><DarkCircles /></Layout>} />
      <Route path="/facial-volume" component={() => <Layout><FacialVolume /></Layout>} />

      {/* ── Injectables ── */}
      <Route path="/injectables" component={() => <Layout><Injectables /></Layout>} />
      <Route path="/botox" component={() => <Layout><Neuromodulators /></Layout>} />
      <Route path="/dermal-fillers" component={() => <Layout><DermalFillers /></Layout>} />
      <Route path="/lip-fillers" component={() => <Layout><LipFillers /></Layout>} />
      <Route path="/kybella" component={() => <Layout><Kybella /></Layout>} />
      <Route path="/pdo-threads" component={() => <Layout><PDOThreads /></Layout>} />
      <Route path="/biostimulators" component={() => <Layout><Biostimulators /></Layout>} />
      <Route path="/chemical-peels" component={() => <Layout><ChemicalPeels /></Layout>} />

      {/* ── Body Contouring ── */}
      <Route path="/body-contouring" component={() => <Layout><BodyContouring /></Layout>} />
      <Route path="/body-sculpting" component={() => <Layout><BodySculpting /></Layout>} />
      <Route path="/renasculpt" component={() => <Layout><RenaSculpt /></Layout>} />
      <Route path="/cryolipolysis" component={() => <Layout><Cryolipolysis /></Layout>} />
      <Route path="/diode-laser-lipolysis" component={() => <Layout><DiodeLaserLipolysis /></Layout>} />
      <Route path="/stubborn-body-fat" component={() => <Layout><StubbornFat /></Layout>} />

      {/* ── Hair ── */}
      <Route path="/hair-growth" component={() => <Layout><HairGrowth /></Layout>} />
      <Route path="/lllt-hair-restoration" component={() => <Layout><LLLTHairRestoration /></Layout>} />
      <Route path="/areslite-hair-removal" component={() => <Layout><AresLiteHairRemoval /></Layout>} />

      {/* ── Intimate Wellness ── */}
      <Route path="/intimate-wellness" component={() => <Layout><IntimateWellness /></Layout>} />
      <Route path="/vaginal-rejuvenation" component={() => <Layout><VaginalRejuvenation /></Layout>} />
      <Route path="/prp-sexual-wellness" component={() => <Layout><PRPSexualWellness /></Layout>} />
      <Route path="/pelvic-floor" component={() => <Layout><PelvicFloor /></Layout>} />

      {/* ── Pain & Recovery ── */}
      <Route path="/pain-regenerative-medicine" component={() => <Layout><PainRegenerativeMedicine /></Layout>} />

      {/* ── Weight Loss & Wellness ── */}
      <Route path="/weight-loss" component={() => <Layout><WeightLoss /></Layout>} />
      <Route path="/wellness" component={() => <Layout><WellnessAntiAging /></Layout>} />
      <Route path="/hormone-optimization" component={() => <Layout><HormoneOptimization /></Layout>} />
      <Route path="/peptide-therapy" component={() => <Layout><PeptideTherapy /></Layout>} />
      <Route path="/functional-lab-testing" component={() => <Layout><FunctionalLabTesting /></Layout>} />

      {/* ── AI Skin Analysis ── */}
      <Route path="/ai-skin-analysis" component={() => <Layout><AISkinAnalysis /></Layout>} />

      {/* ── Blog ── */}
      <Route path="/blog" component={() => <Blog />} />
      <Route path="/blog/:slug" component={() => <BlogPost />} />

      {/* ── Owner Admin ── */}
      <Route path="/admin/leads" component={() => <Layout><LeadsDashboard /></Layout>} />
      <Route path="/admin/blog" component={() => <AdminBlog />} />

      {/* ── Internal MedSpa Management Dashboard ── */}
      <Route path="/admin/dashboard" component={AdminDashboard} />
      <Route path="/admin/appointments" component={AdminAppointments} />
      <Route path="/admin/patients" component={AdminPatients} />
      <Route path="/admin/revenue" component={AdminRevenue} />
      <Route path="/admin/services" component={AdminServices} />
      <Route path="/admin/leads-crm" component={AdminLeads} />
      <Route path="/admin/google-indexing" component={AdminGoogleIndexing} />

      {/* ── AI Context / GEO Entity Page ── */}
      <Route path="/ai-context" component={() => <AIContext />} />

      {/* ── Membership & Info ── */}
      <Route path="/membership" component={() => <Layout><Membership /></Layout>} />
      <Route path="/technology-comparison" component={() => <Layout><TechnologyComparison /></Layout>} />
      <Route path="/privacy-policy" component={() => <Layout><PrivacyPolicy /></Layout>} />
      <Route path="/medical-disclaimer" component={() => <Layout><MedicalDisclaimer /></Layout>} />
      <Route path="/terms-of-service" component={() => <Layout><TermsOfService /></Layout>} />
      <Route path="/contact" component={() => <Layout><Contact /></Layout>} />
      <Route path="/sms-terms" component={() => <Layout><SMSTerms /></Layout>} />

      {/* ── Google Ads Landing Pages (no nav) ── */}
      <Route path="/lp/laser-hair-removal-dallas" component={LaserHairRemovalDallas} />
      <Route path="/lp/rf-microneedling-dallas" component={RFMicroneedlingDallas} />
      <Route path="/lp/ipl-photofacial-dallas" component={IPLPhotofacialDallas} />
      <Route path="/lp/co2-laser-dallas" component={CO2LaserDallas} />
      <Route path="/lp/best-med-spa-dallas" component={BestMedSpaDallas} />
      <Route path="/lp/aquafacial-dallas" component={AquafacialDallas} />
      <Route path="/lp/dermal-fillers-dallas" component={DermalFillersDallas} />
      <Route path="/lp/rf-microneedling-ads" component={RFMicroneedlingAds} />
      <Route path="/lp/prp-hair-dallas" component={PRPHairDallas} />
      <Route path="/lp/facial-balancing-dallas" component={FacialBalancingDallas} />
      <Route path="/lp/liquid-facelift-dallas" component={LiquidFaceliftDallas} />

      {/* ── Brand-Name Alternative SEO Pages ── */}
      <Route path="/morpheus8-alternative-dallas" component={Morpheus8Alternative} />
      <Route path="/bbl-alternative-dallas" component={BBLAlternative} />
      <Route path="/fraxel-alternative-dallas" component={FraxelAlternative} />
      <Route path="/hydrafacial-alternative-dallas" component={AquaFacialAlternative} />
      <Route path="/ultherapy-alternative-dallas" component={UltherapyAlternative} />
      <Route path="/sculptra-alternative-dallas" component={SculptraAlternative} />
      <Route path="/coolpeel-alternative-dallas" component={CoolPeelAlternative} />
      <Route path="/clear-brilliant-alternative-dallas" component={ClearBrilliantAlternative} />
      <Route path="/emsculpt-neo-alternative-dallas" component={EmsculptNeoAlternative} />
      <Route path="/picosure-alternative-dallas" component={PicoSureAlternative} />

      {/* ── Instagram Bio Link & Referral ── */}
      <Route path="/ig" component={InstagramBioLink} />
      <Route path="/booking" component={Booking} />
      <Route path="/referral" component={() => <Layout><Referral /></Layout>} />
      <Route path="/leave-a-review" component={LeaveAReview} />

      {/* ── Authority & Local Area SEO Pages ── */}
      <Route path="/dr-kohli" component={DrKohli} />
      <Route path="/why-choose-us" component={WhyChooseUs} />
      <Route path="/areas-we-serve" component={() => <Layout><AreasWeServe /></Layout>} />
      <Route path="/med-spa-near-me" component={() => <Layout><MedSpaNearMeDallas /></Layout>} />
      <Route path="/non-surgical-face-lift" component={() => <Layout><NonSurgicalFaceLift /></Layout>} />
      <Route path="/facial-balancing" component={() => <Layout><FacialBalancing /></Layout>} />
      <Route path="/xerf-vs-morpheus8-thermage-ultherapy" component={() => <Layout><SkinTighteningCompared /></Layout>} />
      <Route path="/jawline-filler-dallas" component={() => <Layout><JawlineFillerDallas /></Layout>} />
      <Route path="/tear-trough-filler-dallas" component={() => <Layout><TearTroughFillerDallas /></Layout>} />
      <Route path="/nasolabial-folds-filler-dallas" component={() => <Layout><NasolabialFoldsDallas /></Layout>} />
      <Route path="/hand-filler-dallas" component={() => <Layout><HandFillerDallas /></Layout>} />
      <Route path="/turkey-neck-treatment-dallas" component={() => <Layout><TurkeyNeckDallas /></Layout>} />
      <Route path="/gallery" component={() => <Layout><GalleryHub /></Layout>} />
      <Route path="/gallery/:slug" component={() => <Layout><GalleryRoute /></Layout>} />
      <Route path="/packages" component={() => <Layout><PackagesPage /></Layout>} />
      <Route path="/financing" component={() => <Layout><FinancingPage /></Layout>} />
      {/* ── Legacy route ── */}
      <Route path="/devices/:slug" component={() => <Layout><DeviceDetail /></Layout>} />

      {/* ── 404 ── */}
      <Route path="/404" component={NotFound} />
      <Route component={NotFound} />
    </Switch>
    </Suspense>
    </>
  );
}

const DEFERRED_MOUNT_EVENTS = ["pointerdown", "keydown", "touchstart", "scroll"] as const;
const DEFERRED_MOUNT_IDLE_DELAY_MS = 4000;

/**
 * False on the server and during hydration; true after the visitor first
 * interacts, or after a grace period once the main thread is idle.
 */
function useMountAfterInteractionOrIdle() {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let done = false;
    let timer: number | undefined;
    let idleHandle: number | undefined;
    const cleanup = () => {
      DEFERRED_MOUNT_EVENTS.forEach(type => window.removeEventListener(type, go));
      window.clearTimeout(timer);
      if (idleHandle !== undefined && "cancelIdleCallback" in window) window.cancelIdleCallback(idleHandle);
    };
    const go = () => {
      if (done) return;
      done = true;
      cleanup();
      setReady(true);
    };
    DEFERRED_MOUNT_EVENTS.forEach(type => window.addEventListener(type, go, { passive: true, once: true }));
    timer = window.setTimeout(() => {
      if ("requestIdleCallback" in window) idleHandle = window.requestIdleCallback(go, { timeout: 2000 });
      else go();
    }, DEFERRED_MOUNT_IDLE_DELAY_MS);
    return cleanup;
  }, []);
  return ready;
}

/** Patient-facing floating widgets (chat, Cherry financing). Not on the provider-partner recruitment funnel. */
function PatientWidgets() {
  const [location] = useLocation();
  const path = location.split("?")[0].replace(/\/+$/, "");
  if (path === "/provider-partners" || path.startsWith("/provider-partners/")) return null;
  // Ad landing pages carry their own sticky Book/Call bar; the floating Cherry pill and the
  // chat bubble covered it on phones (2026-10-08 ad-destination audit).
  if (path.startsWith("/lp/")) return null;
  // The XERF page is the Google + Meta XERF ad destination and has the same sticky Book/Call/Text
  // bar. Once shown (after 400px of scroll) the Cherry pill stayed put and covered the hero
  // "Book a XERF Consultation" button when a phone visitor scrolled back up (2026-10-08 self-audit).
  if (path === "/xerf-skin-tightening") return null;
  return (
    <Suspense fallback={null}>
      <ChatWidget />
      <CherryFloating />
    </Suspense>
  );
}

function DeferredWidgets() {
  const ready = useMountAfterInteractionOrIdle();
  if (!ready) return null;
  return (
    <>
      <PatientWidgets />
      <Suspense fallback={null}>
        <Toaster position="top-right" richColors />
      </Suspense>
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="light">
        {/* No root TooltipProvider: ui/tooltip's <Tooltip> wraps its own provider, and a
            root one pulled Radix popper + floating-ui into every page's entry chunk. */}
        <Router />
        <DeferredWidgets />
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
